import { billingRedirectSchema, billingStatusSchema } from '@mio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiRequest } from '@/shared/api/client';
import { billingStatusQuery } from '@/shared/api/queries/billing';

import type { BillingPlanId } from '@mio/shared';

export function useBillingStatus(enabled = true) {
  return useQuery({ ...billingStatusQuery, enabled });
}

/** Returns the Stripe-hosted checkout URL; the caller navigates the whole window there. */
export function useStartCheckout() {
  return useMutation({
    mutationFn: (plan: BillingPlanId) =>
      apiRequest('/billing/checkout', billingRedirectSchema, { method: 'POST', body: { plan } }),
  });
}

/** Applies a completed checkout immediately instead of waiting for the webhook. */
export function useConfirmCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiRequest('/billing/checkout/confirm', billingStatusSchema, {
        method: 'POST',
        body: { sessionId },
      }),
    onSuccess: (status) => {
      queryClient.setQueryData(billingStatusQuery.queryKey, status);
      void queryClient.invalidateQueries({ queryKey: ['learner'] });
    },
  });
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: () => apiRequest('/billing/portal', billingRedirectSchema, { method: 'POST' }),
  });
}

export interface CheckoutReturn {
  outcome: 'success' | 'cancelled' | null;
  sessionId: string | null;
}

/** What Stripe appended to the return URL, read once per mount. */
export function useCheckoutReturn(): CheckoutReturn {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    return {
      outcome: checkout === 'success' || checkout === 'cancelled' ? checkout : null,
      sessionId: params.get('session_id'),
    };
  }, []);
}
